/**
 * Sign in / create account (plan §23.2).
 *
 * "Create account -> choose plan -> checkout -> Building your Construct -> Ready."
 * No API keys, no cloud dashboards, no GitHub account. Two ways in: a one-time email link, or
 * Google. Both land back on `/` which routes to wherever the account actually belongs.
 */
import { useState, type FormEvent } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { api } from "../api/client.js";
import { Banner, ErrorNote, Loading, Spinner } from "../components/ui.js";
import { useSession } from "../state/session.js";

/**
 * The API only registers the Google provider when it holds OAuth credentials — a deployment
 * without them serves 404 on the social route, so the button must not exist in that build.
 * Set VITE_GOOGLE_AUTH=1 at build time only when the API deploy carries GOOGLE_CLIENT_ID.
 */
const GOOGLE_AUTH_ENABLED = ["1", "true"].includes(String(import.meta.env.VITE_GOOGLE_AUTH ?? "").toLowerCase());

/**
 * Where to land after signing in.
 *
 * Normally `/`, which routes to wherever the account belongs. But `Protected` records the screen
 * the visitor was actually trying to reach, and some of those carry state that matters: a consent
 * request at `/link?request=…` is a live authorization an agent is waiting on, and sending that
 * person to their dashboard instead would silently abandon it. Only in-app paths are honoured, so
 * the value cannot be turned into an open redirect.
 */
function returnPathFrom(state: unknown): string {
  const from = (state as { from?: unknown } | null)?.from;
  if (typeof from !== "string") return "/";
  return from.startsWith("/") && !from.startsWith("//") ? from : "/";
}

export default function SignIn() {
  const { me, loading } = useSession();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [googleBusy, setGoogleBusy] = useState(false);
  const returnPath = returnPathFrom(location.state);

  if (loading) return <Loading label="Checking your session" />;
  if (me) return <Navigate to={returnPath} replace />;

  const callbackURL = `${window.location.origin}${returnPath}`;

  /**
   * Ask the API where Google wants us, then go there.
   *
   * The navigation is a full page load on purpose: the OAuth handshake leaves this origin and
   * comes back to the API's callback, so it cannot live inside the SPA router. What CAN be a
   * fetch is asking for the URL, and it has to be, because the endpoint is POST-only.
   */
  async function startGoogle() {
    setError(null);
    setGoogleBusy(true);
    try {
      window.location.assign(await api.googleSignInUrl(callbackURL));
    } catch (err) {
      setError(err);
      setGoogleBusy(false);
    }
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSending(true);
    try {
      await api.sendMagicLink(email.trim(), callbackURL);
      setSent(true);
    } catch (err) {
      setError(err);
    } finally {
      setSending(false);
    }
  }

  return (
    <div className="shell">
      <main className="main">
        <div className="wrap--narrow">
          <div className="page-head">
            <p className="eyebrow">The Construct</p>
            <h1>Sign in</h1>
            <p className="lede">
              Private knowledge infrastructure you own. Bring your ideas, conversations, and files
              in; recall them from your agents and apps.
            </p>
          </div>

          {sent ? (
            <div className="card card--accent">
              <h2>Check your email</h2>
              <p className="muted">
                We sent a one-time sign-in link to <strong>{email}</strong>. It works once and
                expires in ten minutes.
              </p>
              <div className="btn-row">
                <button
                  type="button"
                  className="btn btn--ghost"
                  onClick={() => {
                    setSent(false);
                    setError(null);
                  }}
                >
                  Use a different address
                </button>
              </div>
            </div>
          ) : (
            <div className="card">
              {error ? <ErrorNote error={error} /> : null}
              <form onSubmit={onSubmit} noValidate>
                <label className="field">
                  <span className="field__label">Email address</span>
                  <input
                    className="input"
                    type="email"
                    name="email"
                    autoComplete="email"
                    required
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                  <span className="field__hint">
                    New here? Signing in with a new address creates your account.
                  </span>
                </label>
                <div className="btn-row">
                  <button type="submit" className="btn btn--primary" disabled={sending || !email.trim()}>
                    {sending ? <Spinner label="Sending" /> : null}
                    {sending ? "Sending" : "Email me a link"}
                  </button>
                </div>
              </form>

              {GOOGLE_AUTH_ENABLED ? (
                <>
                  <p className="muted small" style={{ marginTop: 26 }}>
                    or
                  </p>
                  <div className="btn-row" style={{ marginTop: 10 }}>
                    <button
                      type="button"
                      className="btn btn--ghost"
                      disabled={googleBusy}
                      onClick={() => void startGoogle()}
                    >
                      {googleBusy ? "Opening Google" : "Continue with Google"}
                    </button>
                  </div>
                </>
              ) : null}
            </div>
          )}

          <div style={{ marginTop: 24 }}>
            <Banner kind="info">
              We never ask for cloud credentials. Your Construct is created and run for you, no
              accounts to set up anywhere else.
            </Banner>
          </div>
        </div>
      </main>
    </div>
  );
}
